import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import {
  getGroups,
  getPermissions, // assumed to exist in your slice
  getRoles,
  createRole,
  updateRole,
  deleteRole,
} from "../../../../../store/roleManagement/roleManagementSlice";
import ResourcePage from "./shared/ResourcePage";
import TagList from "./shared/TagList";
import GroupPermissionCards from "./shared/GroupPermissionCards";

const thunks = { fetch: getRoles, create: createRole, update: updateRole, remove: deleteRole };

const RolesPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const groups = useSelector((s) => s.roleManagement.groups.data);
  const permissions = useSelector((s) => s.roleManagement.permissions.data);

  // groups are needed as options in the create/edit form,
  // permissions are needed to show readable names in the view modal
  useEffect(() => {
    dispatch(getGroups({ page: 1, size: 100 }));
    dispatch(getPermissions({ page: 1, size: 1000 }));
  }, [dispatch]);

  const options = useMemo(
    () => groups.map((g) => ({ value: g._id, label: g.name })),
    [groups]
  );

  // permission key -> display name (adjust `key`/`name` to your permission shape)
  const permissionName = useMemo(
    () => Object.fromEntries(permissions.map((p) => [p.key, p.name])),
    [permissions]
  );

  const groupNames = (row) => (row.groups || []).map((g) => g.name);

  const columns = [
    { key: "name", label: t("pages.role_management.name") },
    {
      key: "groups",
      label: t("pages.role_management.groups"),
      render: (value, row) => <TagList items={groupNames(row)} max={4} />,
    },
  ];

  const viewFields = [
    { key: "name", label: t("pages.role_management.name") },
    {
    key: "groups",
    label: t("pages.role_management.groups"),
    fullWidth: true,
    render: (row) => (
      <VStack align="stretch" spacing={4}>
        {/* <TagList items={groupNames(row)} /> */}
        <GroupPermissionCards groups={row.groups} permissionName={permissionName} />
      </VStack>
    ),
  },
  ];

  const formFields = [
    { key: "name", label: t("pages.role_management.name"), type: "text", required: true },
    {
    key: "groups",
    label: t("pages.role_management.groups"),
    type: "checkboxes",
    options,
    required: true,
    fullWidth: true,
    // values.groups is an array of selected group ids
    renderAfter: (values) => (
      <Box mt={4}>
        <GroupPermissionCards
          groups={groups.filter((g) => (values.groups || []).includes(g._id))}
          permissionName={permissionName}
        />
      </Box>
    ),
  },
  ];

  return (
    <ResourcePage
      resource="roles"
      singular="role"
      columns={columns}
      viewFields={viewFields}
      formFields={formFields}
      thunks={thunks}
      getInitialValues={(row) => ({
        name: row.name ?? "",
        groups: (row.groups || []).map((g) => g._id),
      })}
      toPayload={(v) => ({ name: v.name, groups: v.groups })}
    />
  );
};

export default RolesPage;